import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import LicensePaymentProofSection from "@/components/LicensePaymentProofSection";
import {
  CLIENTS_SCOPE_HELP,
  CLIENTS_SCOPE_OPTIONS,
  COMMERCIAL_SCOPE_INFO_NOTE,
  DEFAULT_ESTIMATED_CLIENTS_RANGE,
  DEFAULT_ESTIMATED_USERS_RANGE,
  USERS_SCOPE_HELP,
  USERS_SCOPE_OPTIONS,
  clientsRangeRequiresCustom,
  usersRangeRequiresCustom,
} from "@/lib/commercialLicenseScope";

function SectionBlock({ title, children, testId }) {
  return (
    <section className="rounded-sm border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid={testId}>
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">{title}</h5>
      {children}
    </section>
  );
}

/**
 * Alcance estimado compartido (web + PDF/manual).
 */
export default function LicenseCommercialScopeForm({
  form,
  setForm,
  disabled = false,
  showComments = true,
  showPaymentProof = true,
}) {
  const usersRange = form.estimated_users_range || DEFAULT_ESTIMATED_USERS_RANGE;
  const clientsRange = form.estimated_clients_range || DEFAULT_ESTIMATED_CLIENTS_RANGE;

  return (
    <>
    <SectionBlock title="Alcance estimado" testId="license-commercial-scope-section">
      <p
        className="text-[11px] text-slate-700 bg-sky-50/80 border border-sky-100 rounded-sm px-2.5 py-2 m-0 leading-snug"
        data-testid="license-commercial-scope-info-note"
      >
        {COMMERCIAL_SCOPE_INFO_NOTE}
      </p>

      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-800">Usuarios que usarán el sistema</Label>
        <p className="text-[10px] text-slate-500 m-0">{USERS_SCOPE_HELP}</p>
        <RadioGroup
          value={usersRange}
          onValueChange={(v) =>
            setForm((f) => ({
              ...f,
              estimated_users_range: v,
              estimated_users: USERS_SCOPE_OPTIONS.find((o) => o.id === v)?.value ?? f.estimated_users,
            }))
          }
          className="space-y-1.5"
          disabled={disabled}
        >
          {USERS_SCOPE_OPTIONS.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 text-[11px] cursor-pointer"
              data-testid={`license-scope-users-${opt.id}`}
            >
              <RadioGroupItem value={opt.id} disabled={disabled} />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
        {usersRangeRequiresCustom(usersRange) ? (
          <div className="max-w-xs pt-1">
            <Label className="text-[10px] text-slate-600">Número de usuarios</Label>
            <Input
              type="number"
              min={usersRange === "6+" ? 6 : 1}
              value={form.estimated_users ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, estimated_users: e.target.value }))}
              disabled={disabled}
              className="mt-1 h-9 text-xs"
              data-testid="license-scope-users-custom"
            />
          </div>
        ) : null}
      </div>

      <div className="space-y-2 pt-2 border-t border-slate-200/80">
        <Label className="text-xs font-medium text-slate-800">Clientes o empresas que administrarás</Label>
        <p className="text-[10px] text-slate-500 m-0">{CLIENTS_SCOPE_HELP}</p>
        <RadioGroup
          value={clientsRange}
          onValueChange={(v) =>
            setForm((f) => ({
              ...f,
              estimated_clients_range: v,
              estimated_clients: CLIENTS_SCOPE_OPTIONS.find((o) => o.id === v)?.value ?? f.estimated_clients,
            }))
          }
          className="space-y-1.5"
          disabled={disabled}
        >
          {CLIENTS_SCOPE_OPTIONS.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 text-[11px] cursor-pointer"
              data-testid={`license-scope-clients-${opt.id}`}
            >
              <RadioGroupItem value={opt.id} disabled={disabled} />
              {opt.label}
            </label>
          ))}
        </RadioGroup>
        {clientsRangeRequiresCustom(clientsRange) ? (
          <div className="max-w-xs pt-1">
            <Label className="text-[10px] text-slate-600">Número de clientes/empresas</Label>
            <Input
              type="number"
              min={clientsRange === "100+" ? 100 : 1}
              value={form.estimated_clients ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, estimated_clients: e.target.value }))}
              disabled={disabled}
              className="mt-1 h-9 text-xs"
              data-testid="license-scope-clients-custom"
            />
          </div>
        ) : null}
      </div>

      {showComments ? (
        <div className="pt-2 border-t border-slate-200/80">
          <Label className="text-xs">Comentarios (opcional)</Label>
          <SpellcheckTextarea
            value={form.comments || ""}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            disabled={disabled}
            rows={2}
            className="mt-1 rounded-sm text-xs resize-none"
            testId="license-scope-comments"
          />
        </div>
      ) : null}
    </SectionBlock>
    {showPaymentProof ? (
      <LicensePaymentProofSection
        form={form}
        setForm={setForm}
        disabled={disabled}
        testId="license-commercial-payment-proof"
      />
    ) : null}
    </>
  );
}
